import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useTranslation } from '../../utils/i18n';
import axios from 'axios';
import {
  Users,
  Activity,
  Clock,
  Laptop,
  Shield,
  AlertTriangle,
  RefreshCw,
  Eye,
  EyeOff,
} from 'lucide-react';
import { Button } from '../../components/ui/button';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

/**
 * Team Session Activity Page - Phase 9.1
 * 
 * Shows team/session activity with level-based visibility:
 * - Auto Starter: No IP address visibility
 * - Higher tiers: Full session data including IP
 */
const TeamActivityPage = () => {
  const { user, token } = useAuth();
  const { language } = useTheme();
  const { t } = useTranslation(language);
  
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Determine user's plan/tier for IP visibility
  // Auto Starter = no IP visibility
  // Higher tiers = IP visible
  const userPlan = user?.plan || user?.subscription_tier || 'auto_starter';
  const canViewIP = !['auto_starter', 'starter', 'free'].includes(userPlan?.toLowerCase());
  const isAdmin = ['platform_owner', 'super_admin', 'owner'].includes(user?.role);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/admin/sessions`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { include_ip: canViewIP },
      });
      setSessions(response.data.sessions || []);
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
      // If endpoint doesn't exist yet, show mock data for demonstration
      if (err.response?.status === 404) {
        setSessions(getMockSessions(canViewIP));
      } else {
        setError(err.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Mock data for demonstration (remove when backend endpoint exists)
  const getMockSessions = (includeIP) => {
    const baseSessions = [
      {
        id: 'sess_001',
        user_name: 'John Smith',
        email: 'john@agency.com',
        ip_address: includeIP ? '192.168.1.100' : null,
        login_time: new Date(Date.now() - 3600000).toISOString(),
        logout_time: null,
        status: 'active',
        device: 'Chrome on macOS',
      },
      {
        id: 'sess_002',
        user_name: 'Sarah Johnson',
        email: 'sarah@agency.com',
        ip_address: includeIP ? '10.0.0.55' : null,
        login_time: new Date(Date.now() - 7200000).toISOString(),
        logout_time: new Date(Date.now() - 1800000).toISOString(),
        status: 'ended',
        device: 'Safari on iOS',
      },
      {
        id: 'sess_003',
        user_name: 'Mike Wilson',
        email: 'mike@agency.com',
        ip_address: includeIP ? '172.16.0.25' : null,
        login_time: new Date(Date.now() - 86400000).toISOString(),
        logout_time: new Date(Date.now() - 82800000).toISOString(),
        status: 'ended',
        device: 'Firefox on Windows',
      },
    ];
    return baseSessions;
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleString(language === 'es' ? 'es-ES' : 'en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!isAdmin) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-6 flex items-start gap-4">
          <AlertTriangle className="text-amber-500 shrink-0 mt-0.5" size={24} />
          <div>
            <h3 className="font-semibold text-amber-800 dark:text-amber-200">
              Access Restricted
            </h3>
            <p className="text-amber-700 dark:text-amber-300 text-sm mt-1">
              Team activity monitoring is only available for administrators.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="team-activity-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 dark:text-white flex items-center gap-3">
            <Activity className="text-emerald-500" size={28} />
            {t('session.title')}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            {t('session.subtitle')}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchSessions}
          disabled={isLoading}
          className="gap-2"
        >
          <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
          Refresh
        </Button>
      </div>

      {/* Plan Notice for Auto Starter */}
      {!canViewIP && (
        <div className="bg-slate-100 dark:bg-stone-800 rounded-xl p-4 flex items-start gap-3" data-testid="ip-restricted-notice">
          <Shield className="text-slate-500 shrink-0 mt-0.5" size={20} />
          <div>
            <p className="text-sm text-slate-700 dark:text-slate-300">
              <strong>Auto Starter Plan:</strong> IP address information is not available on this plan.
              Upgrade to view detailed session information including IP addresses.
            </p>
          </div>
        </div>
      )}

      {/* Sessions Table */}
      <div className="bg-white dark:bg-stone-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="sessions-table">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700">
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {t('session.userName')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {t('session.email')}
                </th>
                {canViewIP && (
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider" data-testid="ip-column-header">
                    {t('session.ipAddress')}
                  </th>
                )}
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {t('session.loginTime')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {t('session.logoutTime')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  {t('session.status')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {isLoading ? (
                <tr>
                  <td colSpan={canViewIP ? 6 : 5} className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center gap-3 text-slate-500">
                      <RefreshCw size={20} className="animate-spin" />
                      {t('common.loading')}
                    </div>
                  </td>
                </tr>
              ) : sessions.length === 0 ? (
                <tr>
                  <td colSpan={canViewIP ? 6 : 5} className="px-6 py-12 text-center text-slate-500 dark:text-slate-400">
                    {t('session.noData')}
                  </td>
                </tr>
              ) : (
                sessions.map((session) => (
                  <tr key={session.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 dark:text-emerald-400 font-semibold text-sm">
                          {session.user_name?.charAt(0) || '?'}
                        </div>
                        <span className="font-medium text-stone-900 dark:text-white">
                          {session.user_name}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-600 dark:text-slate-400">
                      {session.email}
                    </td>
                    {canViewIP && (
                      <td className="px-6 py-4" data-testid={`ip-cell-${session.id}`}>
                        <code className="px-2 py-1 bg-slate-100 dark:bg-slate-700 rounded text-sm text-slate-700 dark:text-slate-300">
                          {session.ip_address || '-'}
                        </code>
                      </td>
                    )}
                    <td className="px-6 py-4 text-slate-600 dark:text-slate-400">
                      <div className="flex items-center gap-2">
                        <Clock size={14} className="text-slate-400" />
                        {formatTime(session.login_time)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-600 dark:text-slate-400">
                      {formatTime(session.logout_time)}
                    </td>
                    <td className="px-6 py-4">
                      {session.status === 'active' ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                          {t('session.active')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400">
                          {t('session.ended')}
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Privacy Notice */}
      <div className="text-center text-sm text-slate-500 dark:text-slate-400">
        <p>Session data is retained for 30 days for security purposes.</p>
      </div>
    </div>
  );
};

export default TeamActivityPage;
